import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Text from '../elements/Typo/Text'
import Dropdown from '../elements/Form/dropdown'

interface TablePaginationProps {
    totalRecords: number
    pageSize: number
    currentPage: number
    onPageChange: (page: number) => void
}

const TablePagination: React.FC<TablePaginationProps> = ({ totalRecords, pageSize, currentPage, onPageChange }) => {
    const { t } = useTranslation()
    const [selectedOption, setSelectedOption] = useState<{ label: string; value: number }>({
        label: '1',
        value: 1,
    })

    // Calculate total pages
    const totalPages = Math.ceil(totalRecords / pageSize)

    // Calculate displayed records range
    const allPages = Array.from({ length: totalPages }, (_, i) => ({
        label: '' + (i + 1),
        value: i + 1,
    }))

    const getPaginationPages = (totalPages: number, currentPage: number, siblingCount: number = 1): (number | string)[] => {
        const pages: (number | string)[] = []

        const totalPageNumbers = siblingCount * 2 + 5

        if (totalPages <= totalPageNumbers) {
            // Show all pages if not too many
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            const leftSibling = Math.max(currentPage - siblingCount, 1)
            const rightSibling = Math.min(currentPage + siblingCount, totalPages)

            const showLeftDots = leftSibling > 2
            const showRightDots = rightSibling < totalPages - 1

            if (!showLeftDots && showRightDots) {
                // Case: no left dots, but right dots
                const leftRange = Array.from({ length: 3 + siblingCount * 2 }, (_, i) => i + 1)
                pages.push(...leftRange, '...', totalPages)
            } else if (showLeftDots && !showRightDots) {
                // Case: left dots, but no right dots
                const rightRange = Array.from({ length: 3 + siblingCount * 2 }, (_, i) => totalPages - (3 + siblingCount * 2) + 1 + i)
                pages.push(1, '...', ...rightRange)
            } else if (showLeftDots && showRightDots) {
                // Case: both sides have dots
                const middleRange = Array.from({ length: rightSibling - leftSibling + 1 }, (_, i) => leftSibling + i)
                pages.push(1, '...', ...middleRange, '...', totalPages)
            }
        }

        return pages
    }

    // Handle page change
    const handlePageChange = (page: number): void => {
        setSelectedOption({
            label: '' + page,
            value: page,
        })

        if (page >= 1 && page <= totalPages && page !== currentPage) {
            onPageChange(page)
        }
    }

    // Don't render pagination if there's only one page or no records
    if (totalRecords <= pageSize || totalRecords === 0) {
        // return null
    }

    useEffect(() => {
        setSelectedOption({
            label: `${currentPage}`,
            value: currentPage,
        })
    }, [currentPage])

    return (
        <div className="flex items-center justify-between bg-white" data-testid="table-custom-pagination">
            {/* Records info */}
            {allPages.length ? (
                <div className="flex items-center mt-2 gap-2">
                    <Text size="xxs">{t('Page')}</Text>
                    <Dropdown isSearchable={false} options={allPages} value={selectedOption} onChange={(e: any) => handlePageChange(e.value)} menuPlacement={'top'} />
                    <Text size="xxs" className="whitespace-nowrap">
                        {t('of')} {allPages.at(-1)?.label}
                    </Text>
                </div>
            ) : (
                <></>
            )}

            {/* Pagination controls */}
            <div className="flex items-cente gap-1.5 mt-2">
                {/* First page button */}
                <button
                    type="button"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className={`rounded-md text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    aria-label={t('Go to first page')}
                >
                    <Text size="xxs">
                        <span className="sr-only">{t('First')}</span>
                    </Text>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-[0.725rem] w-[0.725rem]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M8.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L4.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                </button>

                {/* Previous page button */}
                <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`rounded-md text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    aria-label={t('Go to previous page')}
                >
                    <Text size="xxs">
                        <span className="sr-only">{t('Previous')}</span>
                    </Text>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-[0.725rem] w-[0.725rem]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                </button>

                {/* Page numbers */}
                {getPaginationPages(totalPages, currentPage).map((page, i) =>
                    page === '...' ? (
                        <span key={`ellipsis-${page}-${i == 1 ? 'first' : 'last'}`} className="text-gray-500">
                            ...
                        </span>
                    ) : (
                        (() => {
                            const pageButtonLabel = t('Go to page {{page}}', { page })
                            const resolvedPageButtonLabel = pageButtonLabel.includes('{{page}}') ? `Go to page ${page}` : pageButtonLabel
                            return (
                                <button
                                    type="button"
                                    key={`page-${page}`}
                                    onClick={() => handlePageChange(page as number)}
                                    className={`w-[1.3rem] h-[1.3rem] inline-flex items-center justify-center leading-none rounded-full text-[0.458rem] font-semibold hover:bg-slate-200 transition-all duration-200 border-[#F1F1F1] ${currentPage === page ? 'text-[#F5E003] !bg-slate-600' : 'bg-white  text-secondary border'}`}
                                    aria-label={resolvedPageButtonLabel}
                                >
                                    {page}
                                </button>
                            )
                        })()
                    ),
                )}

                {/* Next page button */}
                <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`rounded-md text-sm ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    aria-label={t('Go to next page')}
                >
                    <Text size="xxs">
                        <span className="sr-only">{t('Next')}</span>
                    </Text>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-[0.725rem] w-[0.725rem]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                </button>

                {/* Last page button */}
                <button
                    type="button"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`rounded-md text-sm ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                    aria-label={t('Go to last page')}
                >
                    <Text size="xxs">
                        <span className="sr-only">{t('Last')}</span>
                    </Text>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-[0.725rem] w-[0.725rem]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 15.707a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L8.586 10l-4.293 4.293a1 1 0 000 1.414z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M11.293 15.707a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L15.586 10l-4.293 4.293a1 1 0 000 1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
        </div>
    )
}

export default TablePagination
